export function generateCSS(state, {
    forPreview = false,
    forCodeView = false
} = {}) {
    const {
        fontFamily,
        fontUrl,
        fontWeight,
        fontSize,
        lineHeight,
        fontEffect,
        fontEffectColor,
        shadowX,
        shadowY,
        shadowBlur,
        fontBold,
        blockNickname,
        boxEffect,
        boxBorder,
        boxEffectColor,
        boxShadowX,
        boxShadowY,
        boxShadowBlur,
        chatBgColor,
        chatTextColor,
        bodyBgColor,
        enableFadeOut,
        fadeDuration,
        chatMarginBottom,
        userIconSize,
        showBadge,
        badgeTop, // 💡 추가
        stickerSize,
        stickerTop, // 💡 추가
        letterSpacing,
        paddingLeftRight,
        paddingTopBottom,
        showNotice,
        noticeText,
        noticeFontSize,
        noticeBgColor,
        noticeHeight,
        chatRadius,
        noticeRadius,
        showNickname,
        nicknameSuffixType,
        nicknameSuffixCustom,
        nicknameSuffixColor,
        noticeTextColor,
        nameBgColor,
        nameColor,
        nameColorCheck,
        align
    } = state;

    function rgbaToCss({ r, g, b, a }) {
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    const actualSuffix = nicknameSuffixType === 'custom' ? nicknameSuffixCustom : nicknameSuffixType;

    /*
     * 💡 2024 치지직 채팅 HTML 업데이트 대응
     * 클래스명이 의미 기반(live_chatting_...)에서 난독화(_word_hash_num)로 바뀜.
     * 해시는 치지직 리빌드마다 변할 수 있어 셀렉터에 포함하지 않고,
     * "오버레이 DOM 안에서 유일한 단어" + "구조(부모-자식 위치)"로만 요소를 특정한다.
     *
     *  채팅 리스트        : [class*=_chatting_]
     *  채팅 한 줄(item)   : [class*=_item_]
     *  메시지 컨테이너    : [class*=_item_] > [class*=_container_]
     *  메시지 wrapper     : [class*=_item_] > [class*=_container_] > [class*=_wrapper_]
     *  메시지 본문        : [class*=_wrapper_] > [class*=_text_]
     *  닉네임 컨테이너    : [class*=_wrapper_] > [class*=_container_]
     *  닉네임 뱃지묶음    : [class*=_wrapper_] > [class*=_container_] > [class*=_wrapper_]
     *  아이콘 슬롯        : [class*=_icon_]
     *  뱃지 감싸개(툴팁)  : [class*=_icon_] > [class*=_container_]
     *  닉네임            : [class*=_nickname_]
     *  닉네임 글자        : [class*=_nickname_] [class*=_text_]
     *  그라디언트 닉네임  : [class*=_has_gradient_]
     *  하이라이트 닉네임  : [class*=_has_highlight_]
     */
    const SEL = {
        chatting: '[class*=_chatting_]',
        item: '[class*=_item_]',
        msgWrapper: '[class*=_item_] > [class*=_container_] > [class*=_wrapper_]',
        msgText: '[class*=_wrapper_] > [class*=_text_]',
        userContainer: '[class*=_wrapper_] > [class*=_container_]',
        userWrapper: '[class*=_wrapper_] > [class*=_container_] > [class*=_wrapper_]',
        icon: '[class*=_icon_]',
        badge: '[class*=_icon_] > [class*=_container_]',
        nickname: '[class*=_nickname_]',
        nameText: '[class*=_nickname_] [class*=_text_]',
        gradient: '[class*=_has_gradient_]',
        // 닉네임색 강제 지정용: 치지직이 그라디언트 닉을 color:transparent !important (명시도 0,3,0)로
        // 고정하므로, 이를 이기려면 동일 명시도(컨테이너+그라디언트+닉네임)가 필요하다.
        gradientNick: '[class*=_container_][class*=_has_gradient_] [class*=_nickname_]',
        highlight: '[class*=_has_highlight_]',
        highlightText: '[class*=_has_highlight_] [class*=_text_]',
    };

    const bodyExtras = forCodeView
        ? 'margin: 0 auto;\n  overflow: hidden;'
        : '';
    const rootDiv = forCodeView
        ? '#'
        : '.';
    const bodySelector = forCodeView
        ? 'body'
        : '.preview-box';
    const chattingHeight = forCodeView
        ? '100vh'
        : '100%';
    const viewchattingHeight = forCodeView
        ? ''
        : `max-height: calc(80vh - 4rem - 77px - ${noticeHeight}px) !important;`;
    const mediachattingHeight = forCodeView
        ? ''
        : `
        @media screen and (max-width: 1400px) {
            .preview-panel ${SEL.chatting} {
                max-height: calc(500px - 4rem - 77px - ${noticeHeight}px) !important;
            }
        }
        `;

    const fontEffectMap = {
        'thin-outline': `
/* 일반 텍스트 및 꼬리말 (paint-order 방식으로 렉 최적화) */
${SEL.msgText},
${SEL.nameText},
${SEL.userContainer}::after {
  -webkit-text-stroke: 5px ${rgbaToCss(fontEffectColor)};
  paint-order: stroke fill;
}

/* 그라디언트 닉네임 (투명도 보존을 위해 기존 drop-shadow 방식 복구) */
${SEL.gradient} ${SEL.nickname} {
  filter: drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 1px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0px);
}

/* 그라디언트 내부 글자의 stroke는 강제 제거하여 그림자가 덮는 현상 방지 */
${SEL.gradient} ${SEL.nameText} {
  -webkit-text-stroke: 0 !important;
}
`,
        'bold-outline': `
/* 일반 텍스트 및 꼬리말 */
${SEL.msgText},
${SEL.nameText},
${SEL.userContainer}::after {
  -webkit-text-stroke: 8px ${rgbaToCss(fontEffectColor)};
  paint-order: stroke fill;
}

/* 그라디언트 닉네임 (투명도 보존) */
${SEL.gradient} ${SEL.nickname} {
  filter: drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 2px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0.2px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0.1px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0px)
  drop-shadow(${rgbaToCss(fontEffectColor)} 0px 0px 0px);
}

${SEL.gradient} ${SEL.nameText} {
  -webkit-text-stroke: 0 !important;
}
`,
        'shadow': `
/* 일반 텍스트 및 꼬리말 그림자 */
${SEL.msgText},
${SEL.nameText},
${SEL.userContainer}::after {
  filter: drop-shadow(${rgbaToCss(fontEffectColor)} ${shadowX}px ${shadowY}px ${shadowBlur}px);
}

/* 그라디언트 닉네임 그림자 */
${SEL.gradient} ${SEL.nickname} {
  filter: drop-shadow(${rgbaToCss(fontEffectColor)} ${shadowX}px ${shadowY}px ${shadowBlur}px);
}
${SEL.gradient} ${SEL.nameText} {
  filter: none !important;
}
`,
        'none': ''
    };

    const boxEffectMap = {
        'outline': `
  border: ${boxBorder}px solid ${rgbaToCss(boxEffectColor)}
`,
        'shadow': `
  box-shadow: ${rgbaToCss(boxEffectColor)} ${boxShadowX}px ${boxShadowY}px ${boxShadowBlur}px;
`,
        'none': ''
    };

    const fontEffect2Map = {
        'thin-outline': `
${SEL.highlightText} {
  -webkit-text-stroke: 5px ${rgbaToCss(fontEffectColor)} !important;
  paint-order: stroke fill;
}
`,
        'bold-outline': `
${SEL.highlightText} {
  -webkit-text-stroke: 8px ${rgbaToCss(fontEffectColor)} !important;
  paint-order: stroke fill;
}
`,
        'shadow': `
${SEL.highlightText} {
  filter: drop-shadow(${rgbaToCss(fontEffectColor)} ${shadowX}px ${shadowY}px ${shadowBlur}px) !important;
}
`,
        'none': ''
    };

    const alignMap = {
        left: `
      align-items: flex-start;
  `,
        center: `
      align-items: center;
  `,
        right: `
      align-items: flex-end;
  `,
    };

    // 💡 여기에 옵셔널 체이닝(?.)을 추가하여 undefined 에러를 방지합니다.
    const isCssImport = fontUrl?.includes('.css') || fontUrl?.includes('family=') || fontUrl?.includes('/css?');

    // 💡 수정된 부분: forPreview가 참일 때는 폰트 로드 구문을 빈 문자열로 반환하여 깜빡임 방지
    const fontFaceConfig = forPreview
        ? ''
        : (isCssImport
            ? `@import ${fontUrl};`
            : `@font-face {
  font-family: '${(fontFamily || '').replace(/['"]/g, '')}'; /* 띄어쓰기가 있는 폰트명을 위해 따옴표 처리 */
  src: ${fontUrl};
  font-weight: ${fontWeight};
  font-style: normal;
  font-display: swap; /* 깜빡임 방지 */
}`);

    return `
${fontFaceConfig}


img,
svg {
    vertical-align: middle;
}
${bodySelector} {
  font-family: '${fontFamily.replace(/['"]/g, '')}';
  background-color: ${rgbaToCss(bodyBgColor)};

  font-weight: ${fontBold ? 'bold' : fontWeight};

  ${bodyExtras}
}
${SEL.chatting} {
  height: ${chattingHeight};
  ${alignMap[align] || ''}
}
${showNotice
            ? `
${rootDiv}root {
  position: relative;
  padding-top: ${noticeHeight}px;
}
${rootDiv}root::before {
  content: '${noticeText}';
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${rgbaToCss(noticeTextColor)};
  background-color: ${rgbaToCss(noticeBgColor)};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  text-align: center;
  font-size: ${noticeFontSize}px !important;
  height: ${noticeHeight}px !important;
  border-radius: ${noticeRadius}px;
}

${SEL.chatting} {
  height: calc(${chattingHeight} - ${noticeHeight}px);
  ${viewchattingHeight}
}

${mediachattingHeight}

`
            : ''}
${SEL.msgWrapper} {
  font-size: ${fontSize}px !important;
  line-height: ${lineHeight}px !important;
  letter-spacing: ${letterSpacing}px !important;
  padding: ${paddingTopBottom}px ${paddingLeftRight}px !important;
}

/* 💡 수정된 부분: 뱃지 위치 조절 (top) */
${SEL.userWrapper} {
  vertical-align: middle;
  position: relative;
  top: ${badgeTop}px;
}

${SEL.item} {
  ${enableFadeOut
            ? `
    opacity: 0;
    animation-name: chatting;
    animation-duration: ${fadeDuration}s;
    animation-direction: normal;
    animation-delay: 0s;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  `
            : ''}
  background-color: ${rgbaToCss(chatBgColor)};
  border-radius: ${chatRadius}px;
  margin-bottom: ${chatMarginBottom}px;
  transform: translateZ(0);
  will-change: transform;
  ${boxEffectMap[boxEffect] || ''}
}

${enableFadeOut
            ? `
@keyframes chatting {
    0%,
    100% {
        opacity: 0
    }
    1%,
    99% {
        opacity: 1
    }
}
@-webkit-keyframes chatting {
    0%,
    100% {
        opacity: 0
    }
    1%,
    99% {
        opacity: 1
    }
}
`
            : ''}
${SEL.icon} img {
  width: ${userIconSize}px;
  height: ${userIconSize}px;
  position: relative;
}
${SEL.icon} {
  vertical-align: top;
  width: auto!important;
  height: auto!important;
}
${SEL.badge} {
  display: block;
}
${showBadge
            ? ''
            : `
${SEL.userWrapper} {
  display: none !important;
}
`}
${showNickname
            ? ''
            : `
${SEL.userContainer} {
  display: none !important;
}
  `}

${actualSuffix ? `
${SEL.userContainer}::after {
  content: "${actualSuffix}";
  white-space: pre;
  color: ${rgbaToCss(nicknameSuffixColor)};
}
${blockNickname ? `${SEL.userContainer}::after { display: none; }` : ''}
` : ''}

${blockNickname
            ? `
            ${SEL.msgWrapper} {
              padding: 0!important;
            }
            ${SEL.userContainer} {
              display: block;
              align-items: center;
              margin-right: 0!important;
              padding: ${paddingTopBottom}px ${paddingLeftRight}px !important;
              border-radius: ${chatRadius}px ${chatRadius}px 0 0;
              background-color: ${rgbaToCss(nameBgColor)}
            }
            ${SEL.msgText} {
              display: block;
              padding: ${
                nameBgColor.a === 0
                  ? `0px ${paddingLeftRight}px ${paddingTopBottom}px !important`
                  : `${paddingTopBottom}px ${paddingLeftRight}px !important`
              };
            }
  `
            : ''}
${nameColorCheck ? `
            ${SEL.nickname},
            ${SEL.gradientNick} {
              color: ${rgbaToCss(nameColor)} !important;
              background: none!important;
            }
            ${SEL.highlight} {
              margin: 0;
              padding: 0;
            }
            ${SEL.highlightText} {
                margin: 0px!important;
                padding: 0px!important;
                border-radius: 6px;
                background: none!important;
            }
                ${fontEffect2Map[fontEffect] || ''}
            ` : ''}
${SEL.msgText} {
  color: ${rgbaToCss(chatTextColor)}!important;
  vertical-align: baseline;
}

/* 💡 수정된 부분: 이모티콘 위치 조절 (top) + 크기 (치지직이 48px를 (0,3,1)로 강제하므로 !important 필수) */
${SEL.msgText} img {
  width: ${stickerSize}px !important;
  height: ${stickerSize}px !important;
  position: relative;
  top: ${stickerTop}px;
}
${fontEffectMap[fontEffect] || ''}
${SEL.highlightText} {
    margin: -2px;
    padding: 2px 4px;
    border-radius: 6px;
}
${SEL.nickname} {
    font-weight: inherit;
}
`.trim();
}
